import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { EventTicketOrder } from './event-ticket-order.js';

/**
 * @deprecated Use {@link OfflineTicketOrderCreatedEvent} instead.
 */
export class PrintedTicketOrderCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '84039e1ef2ea45ccb1890e0ec41261bd';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly order: EventTicketOrder,
  ) {
    super();
  }

  get $typeId(): string {
    return PrintedTicketOrderCreatedEvent.TYPE_ID;
  }

  static create(args: {
      uniqueId: string,
      creatorId: string | null,
      deviceId: string | null,
      creationDate: dayjs.Dayjs,
      source: SyncEventSource,
      order: EventTicketOrder,
  }): PrintedTicketOrderCreatedEvent {
    return new PrintedTicketOrderCreatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.order,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PrintedTicketOrderCreatedEvent {
    return new PrintedTicketOrderCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<EventTicketOrder>(MapUtils.get<SerializedData>(map, 'order')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'orderId': this.order.uniqueId,
      'order': context.toData(this.order),
    };
  }

  clone(): this {
    return new PrintedTicketOrderCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.order.clone(),
    ) as this;
  }

  isSame(other: PrintedTicketOrderCreatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.order, this.order);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof PrintedTicketOrderCreatedEvent;
  }

  static assertType(order: DataModel): order is PrintedTicketOrderCreatedEvent {
    return order instanceof PrintedTicketOrderCreatedEvent;
  }
}

export class PrintedTicketOrderCreatedEventBuilder extends DataModelBuilder<PrintedTicketOrderCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): PrintedTicketOrderCreatedEvent {
    return PrintedTicketOrderCreatedEvent.fromMap(context, map);
  }
}
